import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { InvoiceUserInvoiceTablePlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { BHInvoice } from '@adornis/buchungsheld-db/entities/BHInvoice.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import type { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/internationalization/client/x-tel-input.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { goTo } from '@adornis/router/client/open-href.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { ikhGenerateInvoice, sendIKHInvoiceMails } from '../api/ikh-payment-plugin.js';
import { IKHInvoice } from '../payment/ikh-payment-plugin.js';

const openInvoiceFile = (id: string, file: Maybe<AdornisFile>) => {
  const meta = file?.meta;
  if (!meta) return goTo(AdornisFile.getServeLink(id), { target: '_blank' });
  const fileName = `${meta.fileName}.${meta.extension}`;
  return goTo(AdornisFile.getServeLink(id) + '/' + encodeURIComponent(fileName), { target: '_blank' });
};

export const ikhInvoiceUserInvoicesTable: InvoiceUserInvoiceTablePlugin = {
  id: 'invoice-user-invoice-table::ikh',
  type: 'invoice-user-invoice-table',
  render: (
    host: ChemistryLitElement,
    translation: TranslationController<BHTranslationDictionary>,
    userID: string,
    institutionID: string,
  ) => {
    return html`
      <x-flex space="md">
        <tr-h3> ${translation.translate('user', 'invoices')} </tr-h3>
        <tr-data-view
          .coloring=${host.colors.accent.shade(300)}
          .options=${
            {
              class: BHInvoice as any,
              selectionSet: {
                pdfID: 1,
                pdf: { meta: { fileName: 1, extension: 1 } },
                pdfInternalID: 1,
                pdfInternal: { meta: { fileName: 1, extension: 1 } },
                pdfCorrectionID: 1,
                pdfCorrection: { meta: { fileName: 1, extension: 1 } },
                pdfCorrectionInternalID: 1,
                pdfCorrectionInternal: { meta: { fileName: 1, extension: 1 } },
              },
              isEntryClickable: false,
              isSortable: true,
              view: new View({
                filter: new AdornisFilter({
                  aspects: [
                    new StringAspect({
                      mode: 'equals',
                      fieldName: 'userID',
                      value: userID,
                    }),
                    new StringAspect({
                      mode: 'equals',
                      fieldName: 'institutionID',
                      value: institutionID,
                    }),
                  ],
                }),
              }),
              customColumns: [
                {
                  fieldName: 'courses',
                  columnName: 'Veranstaltung',
                  selectionSet: {
                    ...selectionSet(() => BHInvoice, 4),
                    institutionID: 1,
                    userID: 1,
                    payment: {
                      _id: 1,
                      participations: {
                        _id: 1,
                        product: { _id: 1, name: 1, institutionID: 1, ...{ appointments: { _id: 1, start: 1 } } },
                      },
                    },
                  },
                  renderItem: instance => {
                    return instance.payment.participations
                      .map(participation => participation.product?.name ?? '')
                      .join(', ');
                  },
                },
                {
                  fieldName: 'created',
                  columnName: 'Erstellt',
                },
                {
                  fieldName: 'sentDate',
                  columnName: 'Versandt',
                },
                {
                  customID: '_previewInvoice',
                  columnName: 'Rechnung',
                  renderItem: instance => {
                    return html`
                      <x-flex vertical space="xs">
                        <x-flex space="xs" wrap horizontal>
                          <x-button
                            icon="visibility"
                            .color=${host.colors.accent.shade(300)}
                            mode="text"
                            @click=${() => openInvoiceFile(instance.pdfID, instance.pdf)}
                            >ansehen</x-button
                          >
                        </x-flex>

                        <!-- korrektur -->
                        ${(() => {
                          if (!instance.pdfCorrectionInternalID) return nothing;
                          return html`
                            <x-flex space="xs" wrap horizontal>
                              <x-button
                                icon="visibility"
                                .color=${host.colors.accent.shade(300)}
                                mode="text"
                                @click=${() => openInvoiceFile(instance.pdfCorrectionID, instance.pdfCorrection)}
                                >ansehen (Korrektur)</x-button
                              >
                            </x-flex>
                          `;
                        })()}
                      </x-flex>
                    `;
                  },
                },
                {
                  customID: '_previewInvoiceAccounting',
                  columnName: 'Rechnung (Buchhaltung)',
                  renderItem: instance => {
                    if (!instance.pdfInternalID) return html`-------`;
                    return html`
                      <x-flex vertical space="xs">
                        <x-flex space="xs" wrap horizontal>
                          <x-button
                            icon="visibility"
                            .color=${host.colors.accent.shade(300)}
                            mode="text"
                            @click=${() => openInvoiceFile(instance.pdfInternalID, instance.pdfInternal)}
                            >ansehen</x-button
                          >
                        </x-flex>

                        <!-- korrektur -->
                        ${(() => {
                          if (!instance.pdfCorrectionInternalID) return nothing;
                          return html`
                            <x-flex space="xs" wrap horizontal>
                              <x-button
                                icon="visibility"
                                .color=${host.colors.accent.shade(300)}
                                mode="text"
                                @click=${() =>
                                  openInvoiceFile(instance.pdfCorrectionInternalID, instance.pdfCorrectionInternal)}
                                >ansehen (Korrektur)</x-button
                              >
                            </x-flex>
                          `;
                        })()}
                      </x-flex>
                    `;
                  },
                },
                {
                  customID: '_options',
                  columnName: '',
                  selectionSet: {
                    _id: 1,
                    paymentID: 1,
                    sentDate: 1,
                    payment: {
                      _id: 1,
                      participations: {
                        _id: 1,
                        product: { _id: 1, institutionID: 1 },
                      },
                    },
                  },
                  renderHeader: _ => html`
                    <x-flex
                      space="xs"
                      horizontal
                      center
                      crossaxis-center
                      w100
                      ${css({ color: host.colors.tone.success.shade(800) })}
                    >
                      <tr-icon ${css({ fontSize: `${host.baseFontSize}px` })}>electric_bolt</tr-icon>
                      <tr-text bold ${css({ textWrap: 'nowrap' })}></tr-text>
                    </x-flex>
                  `,
                  renderItem: instance => {
                    const institutionID = instance.payment.participations[0]?.product?.institutionID;
                    if (!institutionID) throw new Error('could not get institutionID');
                    const reference = createRef<XDropdown>();
                    return html` <x-flex horizontal space-between center crossaxis-center>
                      <tr-button-small
                        icon="more_horiz"
                        mode="outline"
                        @click=${async () => {
                          reference.value?.toggle();
                        }}
                      ></tr-button-small>
                      <x-dropdown ${ref(reference)}>
                        <x-flex slot="dropdown-content">
                          <x-card>
                            <x-flex vertical crossaxis-start ${css({ gap: '14px' })}>
                              <!-- neu generieren -->
                              <x-button
                                mode="text"
                                class="hover-text-button"
                                icon="autorenew"
                                @click=${async () => {
                                  await (
                                    await import('@adornis/dialog/x-native-dialog.js')
                                  ).XNativeDialog.waitFor(ikhGenerateInvoice(instance.paymentID, false)());
                                  await (
                                    await import('@adornis/chemistry/elements/components/x-snackbar.js')
                                  ).XSnackbar.show('Die Rechnung wurde neu ausgestellt');
                                }}
                              >
                                Rechnung neu generieren
                              </x-button>

                              <!-- Rechnungskorrektur generieren -->
                              <x-button
                                mode="text"
                                class="hover-text-button"
                                icon="autorenew"
                                @click=${async () => {
                                  await (
                                    await import('@adornis/dialog/x-native-dialog.js')
                                  ).XNativeDialog.waitFor(ikhGenerateInvoice(instance.paymentID, true)());
                                  await (
                                    await import('@adornis/chemistry/elements/components/x-snackbar.js')
                                  ).XSnackbar.show('Die Rechnungskorrektur wurde neu ausgestellt');
                                }}
                              >
                                RechnungsKorrektur generieren
                              </x-button>

                              <x-hr></x-hr>

                              <!-- Rechnung senden -->
                              <x-button
                                mode="text"
                                class="hover-text-button"
                                icon="send"
                                @click=${async () => {
                                  const success = await (
                                    await import('@adornis/dialog/x-native-dialog.js')
                                  ).XNativeDialog.waitFor(sendIKHInvoiceMails(instance._id, false)());
                                  if (success) {
                                    instance.sentDate = new Date();
                                    await instance.save();
                                    (
                                      await import('@adornis/chemistry/elements/components/x-snackbar.js')
                                    ).XSnackbar.show('E-Mail wurde versendet');
                                  } else {
                                    (
                                      await import('@adornis/chemistry/elements/components/x-snackbar.js')
                                    ).XSnackbar.show('es gab einen Fehler beim versenden der E-Mail');
                                  }
                                }}
                              >
                                Rechnung senden
                              </x-button>

                              <!-- Rechnungs Korrektur senden -->
                              <x-button
                                mode="text"
                                class="hover-text-button"
                                icon="send"
                                @click=${async () => {
                                  const success = await (
                                    await import('@adornis/dialog/x-native-dialog.js')
                                  ).XNativeDialog.waitFor(sendIKHInvoiceMails(instance._id, true)());
                                  if (success) {
                                    instance.sentDate = new Date();
                                    await instance.save();
                                    (
                                      await import('@adornis/chemistry/elements/components/x-snackbar.js')
                                    ).XSnackbar.show('E-Mail wurde versendet');
                                  } else {
                                    (
                                      await import('@adornis/chemistry/elements/components/x-snackbar.js')
                                    ).XSnackbar.show('es gab einen Fehler beim versenden der E-Mail');
                                  }
                                }}
                              >
                                Rechnungs-Korrektur senden
                              </x-button>
                            </x-flex>
                          </x-card>
                        </x-flex>
                      </x-dropdown>
                    </x-flex>`;
                  },
                  columnWidth: 'min-content',
                },
              ],
            } satisfies DataViewOptions<typeof IKHInvoice>
          }
        ></tr-data-view>
      </x-flex>
    `;
  },
};
